import React, { useState, useEffect } from 'react';

import { Card, Tooltip } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { withStyles } from '@material-ui/styles';
import Stat from 'components/Stat';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { TASK_TYPES_2 } from 'constants/ProjectConstants';
import { checkIfFilterIsPresent } from 'constants/helper';
import {
  DelayedIcon,
  // EditIcon,
  HighLowIcon,
  CheckIcon,
  ProgressIcon,
  OngoingShuttleIcon,
  StyledIcon,
  UpDownIcon,
} from 'constants/icons';
import theme from 'constants/theme';

import { getTeamTasksStats } from 'services/task';

const FILTER_TYPE = 'STATUS';

export default function Stats({
  tagId,
  tagType,
  durationKey,
  activeFilters,
  addFilter,
  removeFilter,
  tasksLoading,
  refreshData,
  type,
  startTS,
  endTS,
  timeBoundPeriod,
  sideNavSelectedCohort = null,
}) {
  const [loading, setLoading] = useState(false);
  const [statsValues, setStatsValues] = useState({
    completed: 0,
    ongoing: 0,
    delayed: 0,
    unplanned: 0,
    highProgress: 0,
    mediumProgress: 0,
    lowProgress: 0,
    onTrackProgress: 0,
    offTrackProgress: 0,
    atRiskProgress: 0,
    closedProgress: 0,
  });
  const {
    completed,
    ongoing,
    delayed,
    unplanned,
    highProgress,
    mediumProgress,
    lowProgress,
    onTrackProgress,
    offTrackProgress,
    atRiskProgress,
    closedProgress,
  } = statsValues;

  const commonProps = {
    valueVariant: 'h4',
    displayVariant: 'body2',
    bottomGutter: false,
    displayPositioning: 'inline',
    valuePositioning: 'inline',
  };
  const isGoalView = type === TASK_TYPES_2.goal;
  // todo: refactor - use the one defined in the ProjectContants
  const statsData = !isGoalView
    ? [
        {
          id: 'ONGOING',
          value: ongoing || 0,
          display: 'On-going',
          valueColor: theme.palette.icons.green,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.green}70`,
          displayLeftGutter: 1,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.seagreen}
              color={theme.palette.icons.green}
              size="smallest"
            >
              <OngoingShuttleIcon />
            </CircularIcon>
          ),
        },

        {
          id: 'DELAYED',
          value: delayed || 0,
          display: 'Delayed',
          valueColor: theme.palette.icons.red,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.red}70`,
          displayLeftGutter: 1,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.red}
              color={theme.palette.icons.red}
              size="smallest"
            >
              <DelayedIcon />
            </CircularIcon>
          ),
        },
        {
          id: 'COMPLETED',
          value: completed || 0,
          display: 'Completed',
          valueColor: theme.palette.icons.blue,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.blue}70`,
          displayLeftGutter: 1,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.blue.light.v2}
              color={theme.palette.icons.blue}
              size="smallest"
            >
              <CheckIcon />
            </CircularIcon>
          ),
        },
        {
          id: 'UNPLANNED',
          value: unplanned || 0,
          display: 'Unplanned',
          valueColor: theme.palette.icons.grey2,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.grey2}70`,
          displayLeftGutter: 1,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.lightGrey3}
              color={theme.palette.icons.grey2}
              size="smallest"
            >
              <ProgressIcon />
            </CircularIcon>
          ),
        },
      ]
    : [
        {
          id: 'ON_TRACK',
          value: onTrackProgress || 0,
          display: 'On-Track',
          valueColor: theme.palette.icons.green,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.green}70`,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.seagreen}
              color={theme.palette.icons.green}
              size="smallest"
            >
              <HighLowIcon rotate="-90deg" />
            </CircularIcon>
          ),
        },
        {
          id: 'OFF_TRACK',
          value: offTrackProgress || 0,
          display: 'Off-Track',
          valueColor: theme.palette.lightOrange.main,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.lightOrange.main}70`,
          icon: (
            <CircularIcon
              backgroundColor={`${theme.palette.lightOrange.main}30`}
              color={theme.palette.lightOrange.main}
              size="smallest"
            >
              <UpDownIcon rotate="90deg" />
            </CircularIcon>
          ),
        },
        {
          id: 'AT_RISK',
          value: atRiskProgress || 0,
          display: 'At-Risk',
          valueColor: theme.palette.icons.red,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.red}70`,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.red}
              color={theme.palette.icons.red}
              size="smallest"
            >
              <HighLowIcon rotate="90deg" />
            </CircularIcon>
          ),
        },
        {
          id: 'CLOSED',
          value: closedProgress || 0,
          display: 'Closed',
          valueColor: theme.palette.icons.blue,
          displayColor: 'textSecondary',
          borderColor: `${theme.palette.icons.blue}70`,
          icon: (
            <CircularIcon
              backgroundColor={theme.palette.background.blue.light.v2}
              color={theme.palette.icons.blue}
              size="smallest"
            >
              <CheckIcon />
            </CircularIcon>
          ),
        },
      ];

  const getData = () => {
    setLoading(true);
    getTeamTasksStats({
      tagId,
      tagType,
      durationKey,
      startTS,
      endTS,
      filters: activeFilters,
      type,
      timeBounds: timeBoundPeriod ? [timeBoundPeriod] : null,
      selectedCohort: sideNavSelectedCohort,
    })
      .then((res) => {
        setStatsValues(res.entity);
      })
      .catch((err) => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getData();
  }, [tagId, durationKey, type, JSON.stringify(activeFilters), startTS, endTS, timeBoundPeriod]);

  useEffect(() => {
    if (!refreshData) return;
    getData();
  }, [refreshData]);

  const isFilterPresent = (value) => checkIfFilterIsPresent(activeFilters, FILTER_TYPE, value);

  const statClicked = (value, displayValue) => {
    if (isFilterPresent(value)) {
      removeFilter(FILTER_TYPE, value, displayValue);
    } else {
      addFilter(FILTER_TYPE, value, displayValue);
    }
  };

  const toolTipText = (index) => {
    if (activeFilters.find((filter) => filter.value === statsData[index].id)) {
      return 'Click to disable the filter';
    }
    return `Click to filter ${statsData[index].display.toLowerCase()} tasks`;
  };

  return loading ? (
    <Skeleton height="15vh" />
  ) : (
    statsData.map((statData, idx) => (
      <StyledTooltip title={toolTipText(idx)} key={idx}>
        <StatsContainer
          onClick={() => statClicked(statData.id, statData.display)}
          active={isFilterPresent(statData.id)}
          last={idx === statsData.length - 1}
          borderColor={statData.borderColor}
          disabled={tasksLoading}
          elevation={0}
        >
          <StyledStat {...statData} {...commonProps} />
        </StatsContainer>
      </StyledTooltip>
    ))
  );
}

const StatsContainer = styled(Card)`
  display: inline-flex;
  width: 22.75%;
  margin-right: ${(props) => (props.last ? 0 : '3%')};
  cursor: pointer;
  opacity: ${(props) => (props.active ? 1 : 0.8)};
  border: ${(props) => (props.active ? `2px solid ${props.borderColor}` : 'none')};
  position: relative;
  z-index: 2;
  &:hover {
    box-shadow: 2px 2px 5px 0px #0000001c;
    opacity: 1;
  }
  * {
    cursor: pointer;
  }
  ${(props) =>
    props.disableClick &&
    css`
      cursor: initial;
      pointer-events: none;
    `};
  ${(props) =>
    props.disabled &&
    css`
      cursor: initial;
      opacity: 0.5;
      pointer-events: none;
    `};
  padding: 1.25rem 1rem;
  justify-content: space-around;
`;
Stats.propTypes = {
  teamId: PropTypes.string.isRequired,
  durationKey: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

const CircularIcon = styled(StyledIcon)`
  border-radius: 50%;
  border: 1px solid #eeeeee;
  padding: 1rem;
  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${props.backgroundColor};
    `};
`;

const StyledStat = styled(Stat)`
  display: flex;
  justify-content: ${(props) => (props.icon ? 'space-evenly' : 'center')};
  align-items: center;
  width: 90%;
`;

const StyledTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.blue.toolTip,
    fontSize: '0.9rem',
  },
}))(Tooltip);
